// File: /src/components/finance/FinanceDashCards.js

import React, { useMemo } from 'react';
import {
	FaHandHoldingUsd,
	FaMoneyBillWave,
	FaDollarSign,
	FaRegClock,
	FaExclamationTriangle,
	FaChartLine,
	FaArrowUp,
	FaArrowDown,
	FaPercentage,
	FaBalanceScale,
} from 'react-icons/fa';
import '../../styles/finance/FinanceDashCards.css';

import { calculateFinanceKPIs } from '../../utils/financeMetrics';

const FinanceDashCards = ({ financeSummary, kpis: providedKpis }) => {
	const kpis = useMemo(
		() => providedKpis ?? calculateFinanceKPIs(financeSummary),
		[providedKpis, financeSummary],
	);

	const renderIndicator = (value, inverse = false) => {
		const isPositive = inverse ? value < 0 : value > 0;
		return isPositive ? (
			<FaArrowUp className="indicator positive" />
		) : (
			<FaArrowDown className="indicator negative" />
		);
	};

	return (
		<div className="dash-cards">
			<div className="finance-summary grid-2x3">
				<div className="finance-card income">
					<div className="card-header">
						<div className="card-icon">
							<FaHandHoldingUsd />
						</div>
						<h2>Receitas</h2>
					</div>
					<p>
						R${' '}
						{financeSummary.totalIncome?.toLocaleString('pt-BR', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</p>
				</div>
				<div className="finance-card expense">
					<div className="card-header">
						<div className="card-icon">
							<FaMoneyBillWave />
						</div>
						<h2>Despesas</h2>
					</div>
					<p>
						R${' '}
						{financeSummary.totalExpense?.toLocaleString('pt-BR', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</p>
				</div>
				<div className="finance-card received">
					<div className="card-header">
						<div className="card-icon">
							<FaDollarSign />
						</div>
						<h2>Faturas Pagas</h2>
					</div>
					<p>
						R${' '}
						{financeSummary.totalReceived?.toLocaleString('pt-BR', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</p>
				</div>
				<div className="finance-card pending">
					<div className="card-header">
						<div className="card-icon">
							<FaRegClock />
						</div>
						<h2>Faturas Pendentes</h2>
					</div>
					<p>
						R${' '}
						{financeSummary.totalPending?.toLocaleString('pt-BR', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</p>
				</div>
				<div className="finance-card overdue">
					<div className="card-header">
						<div className="card-icon">
							<FaExclamationTriangle />
						</div>
						<h2>Faturas Atrasadas</h2>
					</div>
					<p>
						R${' '}
						{financeSummary.totalOverdue?.toLocaleString('pt-BR', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</p>
				</div>
				<div
					className={`finance-card net-total ${
						financeSummary.netTotal >= 0 ? 'positive' : 'negative'
					}`}
				>
					<div className="card-header">
						<div className="card-icon">
							<FaChartLine />
						</div>
						<h2>Saldo Efetivo</h2>
					</div>
					<p className="finance-card-net-total-description">
						(Receitas + Faturas Pagas) - (Despesas + Faturas
						Atrasadas)
					</p>
					<p>
						R${' '}
						{financeSummary.netTotal?.toLocaleString('pt-BR', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</p>
				</div>
			</div>

			<div className="kpis-section">
				<h2>Indicadores-Chave de Performance (KPIs)</h2>
				<div className="kpis-grid">
					<div className="kpi-card">
						<div className="kpi-icon">
							<FaHandHoldingUsd />
						</div>
						<div className="kpi-content">
							<h3>Receita Total</h3>
							<div className="kpi-value">
								R${' '}
								{kpis.revenueTotal.toLocaleString('pt-BR', {
									minimumFractionDigits: 2,
								})}
								{renderIndicator(kpis.revenueTotal)}
							</div>
							<p className="kpi-description">
								Receitas + Faturas Pagas
							</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaChartLine />
						</div>
						<div className="kpi-content">
							<h3>Margem Operacional</h3>
							<div className="kpi-value">
								{kpis.operationalMargin.toFixed(2)}%
								{renderIndicator(kpis.operationalMargin)}
							</div>
							<p className="kpi-description">
								Lucro/Receita Total
							</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaBalanceScale />
						</div>
						<div className="kpi-content">
							<h3>Taxa de Conversão</h3>
							<div className="kpi-value">
								{kpis.invoiceConversionRate.toFixed(2)}%
								{renderIndicator(kpis.invoiceConversionRate)}
							</div>
							<p className="kpi-description">
								Faturas Pagas/Total
							</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaPercentage />
						</div>
						<div className="kpi-content">
							<h3>Taxa de Inadimplência</h3>
							<div className="kpi-value">
								{kpis.defaultRate.toFixed(2)}%
								{renderIndicator(kpis.defaultRate, true)}
							</div>
							<p className="kpi-description">
								Faturas Atrasadas/Total
							</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaBalanceScale />
						</div>
						<div className="kpi-content">
							<h3>Índice de Despesas</h3>
							<div className="kpi-value">
								{kpis.expenseRatio.toFixed(2)}%
								{renderIndicator(kpis.expenseRatio, true)}
							</div>
							<p className="kpi-description">
								Despesas/Receita Total
							</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaRegClock />
						</div>
						<div className="kpi-content">
							<h3>Faturas Pendentes</h3>
							<div className="kpi-value">
								{kpis.pendingRatio.toFixed(2)}%
								{renderIndicator(kpis.pendingRatio, true)}
							</div>
							<p className="kpi-description">
								Pendentes/Total Faturas
							</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaHandHoldingUsd />
						</div>
						<div className="kpi-content">
							<h3>Índice de Liquidez</h3>
							<div className="kpi-value">
								{kpis.liquidityRatio.toFixed(2)}
								{renderIndicator(kpis.liquidityRatio)}
							</div>
							<p className="kpi-description">Receita/Despesa</p>
						</div>
					</div>

					<div className="kpi-card">
						<div className="kpi-icon">
							<FaChartLine />
						</div>
						<div className="kpi-content">
							<h3>Média Diária</h3>
							<div className="kpi-value">
								R${' '}
								{kpis.dailyAverageRevenue.toLocaleString(
									'pt-BR',
									{
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									},
								)}
								{renderIndicator(kpis.dailyAverageRevenue)}
							</div>
							<p className="kpi-description">
								Receita Média por Dia
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FinanceDashCards;
